import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';

import type { Observable } from 'rxjs';
import { startWith, switchMap } from 'rxjs';

import type {
	Alignment,
	VerticalAlignment,
} from '@valk-nx/components/ui-hero-item/src/lib/hero-item.interface';
import type { Language } from '@valk-nx/core/lib/core';
import { fallbackLanguage, THEME } from '@valk-nx/core/lib/core';
import type { ContentHotelInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import { AvailabilityWidgetComponent } from '@valk-nx/storyblok/components/availability-widget/src/lib/availability-widget';
import { CarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import type { Carousel } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

@Component({
	selector: 'sb-carousel-with-availability-widget',
	templateUrl: './carousel-with-availability-widget.html',
	imports: [
		CommonModule,
		CarouselComponent,
		AvailabilityWidgetComponent,
		StoryblokDirective,
	],
})
export class CarouselWithAvailabilityWidgetComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	readonly currentLanguage = signal<Language>(fallbackLanguage);
	readonly hotelStore = inject(HotelStore);

	blocks = input<Carousel[]>([]);
	hotelGUID = input<string | undefined>(undefined);
	hotelSlug = input<string | undefined>(undefined);
	openInNewTab = input<boolean>(false);
	includeVhiHotels = input<boolean>(false);

	readonly carousel = computed(() =>
		this.blocks().find((x) => x.component === 'carousel'),
	);

	/**
	 * The centered hero is a resort-theme treatment, and this is the only component
	 * that knows the hero carries an availability widget — see VPL-48443.
	 */
	private readonly isResortTheme =
		inject(THEME, { optional: true }) === 'resort-theme';

	readonly heroAlignment: Alignment = this.isResortTheme ? 'center' : 'left';
	readonly heroVerticalAlignment: VerticalAlignment = this.isResortTheme
		? 'center'
		: 'bottom';

	readonly hotel$: Observable<ContentHotelInterface> =
		this.translate.onLangChange.pipe(
			startWith({ lang: this.translate.getCurrentLang() }),
			switchMap((event) => this.contentService.getHotelInfo(event.lang)),
		);

	constructor() {
		super();
		this.currentLanguage.set(
			(this.translate.getCurrentLang() ??
				this.translate.getFallbackLang()) as Language,
		);
	}
}
